import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Download, X, Search, Users, FileText } from 'lucide-react';
import { posApi } from '../services/api';
import { POSSupplier } from '../types';
import ReportDateRangeFilter from './reports/ReportDateRangeFilter';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import { formatMoney, parseCalendarDate } from '@/utils/currency';

interface LedgerModalProps {
  isOpen: boolean;
  onClose: () => void;
  suppliers: POSSupplier[];
  initialSupplier?: string;
}

interface LedgerEntry {
  posting_date: string;
  invoice_no: string;
  payment_no: string;
  description: string;
  items_string?: string;
  debit: number;
  credit: number;
  balance: number;
}

interface LedgerResponse {
  party_name: string;
  opening_balance: number;
  closing_balance: number;
  balance_label: string;
  entries: LedgerEntry[];
  from_date: string;
  to_date: string;
}

const formatDate = (d?: string) => {
  if (!d) return '';
  try {
    // SD-UK-038: YYYY-MM-DD as local calendar date (avoid UTC midnight −1 day)
    const parsed = parseCalendarDate(d);
    if (!parsed) return d;
    return format(parsed, 'd-MMM-yyyy');
  } catch {
    return d;
  }
};

export const SupplierLedgerModal: React.FC<LedgerModalProps> = ({ isOpen, onClose, suppliers: suppliersProp, initialSupplier }) => {
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const [selectedSupplier, setSelectedSupplier] = useState(initialSupplier || '');
  const [selectedSupplierLabel, setSelectedSupplierLabel] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const supplierDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialSupplier && suppliersProp) {
      setSelectedSupplier(initialSupplier);
      const cust = suppliersProp.find((c) => c.name === initialSupplier);
      if (cust) {
        setSelectedSupplierLabel(cust.mobile ? `${cust.supplier_name} - ${cust.mobile}` : cust.supplier_name);
      }
    } else {
      setSelectedSupplier('');
      setSelectedSupplierLabel('');
    }
  }, [initialSupplier, suppliersProp, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(event.target as Node)) {
        setShowSupplierDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectSupplier = (supplier?: POSSupplier) => {
    if (!supplier) {
      setSelectedSupplier('');
      setSelectedSupplierLabel('');
    } else {
      setSelectedSupplier(supplier.name);
      setSelectedSupplierLabel(
        supplier.mobile ? `${supplier.supplier_name} - ${supplier.mobile}` : supplier.supplier_name
      );
    }
    setSupplierSearch('');
    setShowSupplierDropdown(false);
  };

  const filteredSuppliers = suppliersProp.filter(c => 
    c.supplier_name?.toLowerCase().includes(supplierSearch.toLowerCase()) ||
    c.mobile?.toLowerCase().includes(supplierSearch.toLowerCase())
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['partyLedger', 'POS Supplier', selectedSupplier, from_date, to_date],
    queryFn: async () => {
      const res = await posApi.getPartyLedger({
        party_type: 'POS Supplier',
        party: selectedSupplier || '',
        from_date,
        to_date,
      });
      return (res.data.message || {}) as LedgerResponse;
    },
    enabled: isOpen,
    staleTime: 0,
  });

  const downloadCSV = () => {
    if (!data) return;
    const lines = [
      'Date,Invoice #,Payment #,Description,Debit,Credit,Balance',
      `,,,Opening Balance,,,${data.opening_balance ?? 0}`,
      ...(data.entries || []).map((row) =>
        [
          formatDate(row.posting_date),
          row.invoice_no || '',
          row.payment_no || '',
          `"${(row.description || '').replace(/"/g, '""')}"`,
          row.debit || 0,
          row.credit || 0,
          row.balance ?? 0,
        ].join(',')
      ),
      `,,,Closing Balance,,,${data.closing_balance ?? 0}`,
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute(
      'download',
      `supplier_ledger_${selectedSupplier}_${from_date}_${to_date}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  const closing = data?.closing_balance ?? 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-6xl rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Supplier Ledger Report</h2>
            <p className="text-sm text-gray-500 mt-1">View and export detailed supplier transaction history</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 border-b border-gray-100 space-y-4">
          <div className="flex flex-wrap gap-4 items-end">
            <ReportDateRangeFilter
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              customFrom={customFrom}
              customTo={customTo}
              onCustomFromChange={setCustomFrom}
              onCustomToChange={setCustomTo}
            />

            <div className="space-y-1.5 flex-1 min-w-[200px] sm:min-w-[240px] relative" ref={supplierDropdownRef}>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400" />
                Supplier
              </label>
              {selectedSupplier ? (
                <div className="input-field flex items-center justify-between gap-2 py-2 bg-gray-50 border border-gray-200 shadow-sm rounded-lg">
                  <span className="truncate text-sm text-gray-900 font-medium px-1">{selectedSupplierLabel}</span>
                  <button
                    type="button"
                    onClick={() => handleSelectSupplier()}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                    aria-label="Clear supplier"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      className="input-field pl-9"
                      placeholder="Search by name or phone..."
                      value={supplierSearch}
                      onChange={(e) => {
                        setSupplierSearch(e.target.value);
                        setShowSupplierDropdown(true);
                      }}
                      onFocus={() => setShowSupplierDropdown(true)}
                    />
                  </div>
                  {showSupplierDropdown && (
                    <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                      {filteredSuppliers.length > 0 ? (
                        filteredSuppliers.map((c) => (
                          <button
                            key={c.name}
                            type="button"
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => handleSelectSupplier(c)}
                          >
                            <div className="font-medium">{c.supplier_name}</div>
                            {c.mobile && <div className="text-xs text-gray-500">{c.mobile}</div>}
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">No suppliers found</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex gap-2 w-full sm:w-auto mt-4 sm:mt-0 ml-auto">
              <button
                onClick={downloadCSV}
                disabled={isLoading || !data?.entries?.length}
                className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-gray-50/30">
          {isLoading || isFetching ? (
            <div className="flex items-center justify-center h-full p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : data ? (
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-500 bg-white uppercase sticky top-0 shadow-sm z-10 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 font-medium whitespace-nowrap">Date</th>
                  <th className="px-6 py-4 font-medium">Invoice No</th>
                  <th className="px-6 py-4 font-medium">Payment No</th>
                  <th className="px-6 py-4 font-medium">Description</th>
                  <th className="px-6 py-4 font-medium text-right text-red-600">Debit</th>
                  <th className="px-6 py-4 font-medium text-right text-emerald-600">Credit</th>
                  <th className="px-6 py-4 font-medium text-right">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                <tr className="bg-gray-50/50">
                  <td colSpan={6} className="px-6 py-3 font-medium text-gray-600 text-right">Opening Balance</td>
                  <td className="px-6 py-3 text-right font-semibold text-gray-900">
                    {formatMoney(data.opening_balance ?? 0)}
                  </td>
                </tr>
                {(data.entries || []).map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-6 py-3 whitespace-nowrap text-gray-500">
                      {formatDate(row.posting_date)}
                    </td>
                    <td className="px-6 py-3 text-gray-900 font-medium">
                      {row.invoice_no || '-'}
                    </td>
                    <td className="px-6 py-3 text-gray-900 font-medium">
                      {row.payment_no || '-'}
                    </td>
                    <td className="px-6 py-3 text-gray-600 max-w-xs xl:max-w-md">
                      <div className="font-medium text-gray-900">{row.description}</div>
                    </td>
                    <td className="px-6 py-3 text-right text-red-600">
                      {row.debit > 0 ? formatMoney(row.debit) : '-'}
                    </td>
                    <td className="px-6 py-3 text-right text-emerald-600">
                      {row.credit > 0 ? formatMoney(row.credit) : '-'}
                    </td>
                    <td className="px-6 py-3 text-right font-medium text-gray-900">
                      {formatMoney(row.balance)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 border-t-2 border-gray-200">
                  <td colSpan={4} className="px-6 py-4">
                    <div className="flex justify-end items-center gap-2">
                      <span className="font-medium text-gray-600">Closing Balance:</span>
                      <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded-md font-medium">
                        {data.balance_label}
                      </span>
                    </div>
                  </td>
                  <td colSpan={3} className="px-6 py-4 text-right">
                    <span className={`text-lg font-bold ${closing > 0 ? 'text-red-600' : closing < 0 ? 'text-emerald-600' : 'text-gray-900'}`}>
                      {formatMoney(Math.abs(closing))}
                    </span>
                    {closing !== 0 && (
                      <span className="text-xs font-medium text-gray-500 ml-1">
                        {closing > 0 ? 'DR' : 'CR'}
                      </span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center text-gray-500">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Transactions Found</h3>
              <p>There are no ledger entries for the selected period.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplierLedgerModal;
